import Button from '@components/Button';
import { Collapse } from '@components/Collapse';
import { sendSuccess } from '@components/Noti';
import { showSceneModal } from '@modules/category';
import { $sceneMapApi } from '@stores/dataStores';
import {
  type KrpPlayer,
  MatrixFovType,
  type SceneData,
  SceneType,
} from '@types';
// @ts-ignore
import { KrpanoPlayer } from 'pano-sdk';
import { useEffect, useRef } from 'react';
import { useHover } from 'react-use';
import { loadScene } from '../../player';
import SidebarApplyTo from '../components/SidebarApplyTo';

interface Props {
  data: SceneData;
  onTurnViewClick: () => void;
}

function InitialView({ data, onTurnViewClick }: Props) {
  const previewRef = useRef<KrpPlayer | null>(null);
  const { view, id, type, materialId } = data;
  useEffect(() => {
    return () => {
      previewRef.current?.removePlayer();
    };
  }, []);

  useEffect(() => {
    if (!previewRef.current) {
      const player = new KrpanoPlayer() as KrpPlayer;
      player.createPlayer('pano-view', {}, () => {
        previewRef.current = player;
        player.enableControl(false);
        loadScene({ player, data });
      });
    } else {
      loadScene({ player: previewRef.current, data });
    }
  }, [id, materialId]);

  useEffect(() => {
    if (type === SceneType.pano) {
      previewRef.current?.updateView({
        fov: view.fov,
        fovmin: view.fovMin,
        fovmax: view.fovMax,
        hlookat: view.hLookAt,
        hlookatmin: view.hLookAtMin,
        hlookatmax: view.hLookAtMax,
        vlookat: view.vLookAt,
        vlookatmin: view.vLookAtMin,
        vlookatmax: view.vLookAtMax,
      });
    } else if (type === SceneType.matrix) {
      previewRef.current?.updateView({
        flatFovType: view.flatFovType,
        fov: view.fov,
        fovmin: view.fovMin,
        fovmax: view.fovMax,
        hlookat: view.hLookAt,
        vlookat: view.vLookAt,
      });
    }
  }, [type, view]);

  const handleApplyTo = async () => {
    const { idSet } = await showSceneModal({
      title: '选择要应用到的场景',
      subTitle: '注：只能应用到同类型场景中',
      allowedTypes: [type],
      multiple: true,
    });
    if (!idSet) return;
    $sceneMapApi.update((draft) => {
      Array.from(idSet).forEach((_id) => {
        draft.get(_id)!.view.fov = view.fov;
        draft.get(_id)!.view.hLookAt = view.hLookAt;
        draft.get(_id)!.view.vLookAt = view.vLookAt;
      });
    });
    sendSuccess('视角设置已应用到所选场景');
  };

  const [container] = useHover((isHovered) => (
    <div className="relative flex h-100 w-full items-center justify-center">
      <div id="pano-view" className="h-full w-full" />
      {isHovered &&
        !(
          type === SceneType.matrix &&
          view.flatFovType === MatrixFovType.fullscreen
        ) && (
          <Button
            className="absolute"
            size="sm"
            scheme="gray"
            onClick={onTurnViewClick}
          >
            定位到初始视角
          </Button>
        )}
    </div>
  ));

  return (
    <Collapse title="当前初始视角">
      {container}
      {type !== SceneType.matrix && (
        <SidebarApplyTo
          title="把初始视角应用到..."
          className="mt-20"
          onClick={handleApplyTo}
        />
      )}
    </Collapse>
  );
}

export default InitialView;
